import React, { useState } from "react";
import styles from "./index.module.scss";

const host = window.location.origin.includes("localhost")
  ? "https://localhost:8443"
  : "https://api.puipi.com";

const MAX_DIMENSION = 1440;
const TARGET_MAX_BYTES = 450 * 1024;
const INITIAL_QUALITY = 0.82;
const MIN_QUALITY = 0.42;

const canvasToBlob = (canvas, mimeType, quality) =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });

const loadImageFile = (file) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load image"));
    };
    image.src = objectUrl;
  });

const getResizedDimensions = (width, height, maxDimension) => {
  if (Math.max(width, height) <= maxDimension) {
    return { width, height };
  }
  const ratio = width / height;
  if (ratio >= 1) {
    return { width: maxDimension, height: Math.round(maxDimension / ratio) };
  }
  return { width: Math.round(maxDimension * ratio), height: maxDimension };
};

const compressImageBeforeUpload = async (file) => {
  if (!file || !file.type.startsWith("image/")) {
    return file;
  }
  const image = await loadImageFile(file);
  let { width, height } = getResizedDimensions(image.width, image.height, MAX_DIMENSION);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    return file;
  }

  let bestBlob = null;
  let quality = INITIAL_QUALITY;
  let attempts = 0;

  while (attempts < 10) {
    canvas.width = width;
    canvas.height = height;
    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    let blob = await canvasToBlob(canvas, "image/webp", quality);
    if (!blob) {
      blob = await canvasToBlob(canvas, "image/jpeg", quality);
    }
    if (!blob) {
      break;
    }
    if (!bestBlob || blob.size < bestBlob.size) {
      bestBlob = blob;
    }
    if (blob.size <= TARGET_MAX_BYTES) {
      bestBlob = blob;
      break;
    }
    if (quality > MIN_QUALITY) {
      quality = Math.max(MIN_QUALITY, quality - 0.1);
    } else {
      width = Math.max(360, Math.round(width * 0.85));
      height = Math.max(360, Math.round(height * 0.85));
    }
    attempts += 1;
  }

  if (!bestBlob || bestBlob.size >= file.size) {
    return file;
  }

  const fileExt = bestBlob.type === "image/webp" ? "webp" : "jpg";
  const fileNamePrefix = (file.name || "pet-photo").replace(/\.[^.]+$/, "");
  return new File([bestBlob], `${fileNamePrefix}.${fileExt}`, {
    type: bestBlob.type,
    lastModified: Date.now(),
  });
};

const FileUpload = ({ sendMessage }) => {
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [statusText, setStatusText] = useState("");

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
    setUploadStatus("idle");
    setStatusText("");
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    if (!file) {
      return;
    }
    setUploadStatus("uploading");
    setStatusText("圖片壓縮中...");

    let fileToUpload = file;
    try {
      fileToUpload = await compressImageBeforeUpload(file);
    } catch (error) {
      console.error("compress image failed", error);
    }

    const formData = new FormData();
    formData.append("file", fileToUpload);

    fetch(`${host}/upload`, {
      method: "POST",
      body: formData,
    })
      .then((response) => response.json())
      .then((resp) => {
        setUploadStatus("idle");
        if (resp.success) {
          sendMessage(resp.link);
          setFile(null);
          formElement.reset();
          setStatusText("圖片已送出到聊天室。");
        } else {
          setUploadStatus("error");
          setStatusText(resp.reason || "上傳失敗，請稍後重試。");
        }
      })
      .catch(() => {
        setUploadStatus("error");
        setStatusText("上傳失敗，請稍後重試。");
      });
  };

  return (
    <div className={styles.uploadBar}>
      <form onSubmit={handleFormSubmit} className={styles.form}>
        <label className={styles.filePicker} htmlFor="pet-photo-upload">
          <span className={styles.fileIcon}>📷</span>
          <span className={styles.fileName}>
            {file ? file.name : "選擇毛孩、寵物照片（png/jpg/jpeg/webp）"}
          </span>
          <input
            id="pet-photo-upload"
            type="file"
            onChange={handleFileChange}
            className={styles.hiddenInput}
            accept="image/*"
          />
        </label>
        <button
          disabled={uploadStatus === "uploading" || !file}
          type="submit"
          className={styles.uploadButton}
        >
          {uploadStatus === "uploading" ? "上傳中..." : "上傳圖片"}
        </button>
      </form>
      {statusText && (
        <div
          role="status"
          className={uploadStatus === "error" ? styles.errorText : styles.successText}
        >
          {statusText}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
